import React from 'react';

import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';

import useUserHook from 'hooks/useUserHook';

import { PageNotFound } from 'components/common/ErrorPages/pageNotFound';
import GuardedRoute from 'components/common/GuardedRoute/guardedRoute';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import NoAccess from 'components/common/NoAccess/noAccess';
import { SideMenuListProps } from 'components/common/SideMenu/SideMenu';

import { riskConfig } from 'components/Dashboards/ExcellenceApps/riskConfig';
import { RedirectToLast } from 'components/Dashboards/Program/ProgramWrapper/redirectToLast';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Responsive } from 'components/Dashboards/responsive';
import CriticalityAndRiskPage from 'components/pages/CriticalityAndRiskPage';
import { FinishDatePredictionPage } from 'components/pages/FinishDatePredictionPage';
import { ProductivityPage } from 'components/pages/ProductivityPage';
import { RecommendationsPage } from 'components/pages/RecommendationsPage';
import { ResourcesAnalysisPage } from 'components/pages/ResourcesAnalysis.page';
import { RiskRegisterPage } from 'components/pages/RiskRegisterPage';
import { TrendsPage } from 'components/pages/TrendsPage';

export const sideMenu: SideMenuListProps[] = [
    {
        ...riskConfig.riskRegister,
        id: 'riskRegister',
        muiIcon: <MuiIcon icon={'crisis_alert'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'RiskRegister',
    },
    {
        ...riskConfig.kcRecommendations,
        id: 'Recommendations',
        muiIcon: <MuiIcon icon={'batch_prediction'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'Recommendations',
    },
    {
        ...riskConfig.criticalityAnalysis,
        id: 'CriticalityAnalysis',
        muiIcon: (
            <MuiIcon icon={'assignment_late'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />
        ),
        permissions: 'CriticalityAnalysis',
    },
    {
        ...riskConfig.finishDatePrediction,
        id: 'finishDatePrediction',
        muiIcon: <MuiIcon icon={'event'} type={'outlined'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'FinishDatePrediction',
    },
    {
        ...executionConfig.productivity,
        id: 'Productivity',
        muiIcon: <MuiIcon icon={'speed'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'RunRate',
    },
    {
        ...executionConfig.resourcesAnalysis,
        id: 'ResourcesAnalysis',
        muiIcon: <MuiIcon icon={'handyman'} type={'outlined'} fontSize={'2.3rem'} color={'var(--text-primary)'} />,
        permissions: 'ResourcesAllocation',
    },
    {
        ...executionConfig.crossVersionsAnalysis,
        id: 'CrossVersionsAnalysis',
        muiIcon: <MuiIcon icon={'ssid_chart'} type={'round'} fontSize={'2.5rem'} color={'var(--text-primary)'} />,
        permissions: 'TrendAnalysis',
    },
];

export const ExcellenceAppsRoutes = () => {
    const { path } = useRouteMatch();
    const { ability } = useUserHook();
    const RecommendationsAbility = ability.can('view', 'Recommendations');
    const FinishDatePredictionAbility = ability.can('view', 'FinishDatePrediction');
    const redirect = sideMenu.find((item) => {
        return item.permissions && ability.can('view', item.permissions);
    });

    //TODO: create component for wrapping just routes that contains the view (programWrapper, SideDashboardMenu, mainColumn, TopHeaderContainer)
    return (
        <Responsive sideMenu={sideMenu} title={'Risk'}>
            <Switch>
                <GuardedRoute
                    path={`${path}/${riskConfig.kcRecommendations.link}/:id/:versionToCompareId?`}
                    component={RecommendationsPage}
                    auth={RecommendationsAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.kcRecommendations.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${riskConfig.kcRecommendations.link}`}
                            title={riskConfig.kcRecommendations.title}
                        />
                    )}
                    auth={RecommendationsAbility}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${riskConfig.finishDatePrediction.link}/:id`}
                    component={FinishDatePredictionPage}
                    auth={FinishDatePredictionAbility}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.finishDatePrediction.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${riskConfig.finishDatePrediction.link}`}
                            title={riskConfig.finishDatePrediction.title}
                        />
                    )}
                    auth={FinishDatePredictionAbility}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${riskConfig.riskRegister.link}/:id`}
                    component={RiskRegisterPage}
                    auth={ability.can('view', 'RiskRegister')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${riskConfig.riskRegister.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${riskConfig.riskRegister.link}`}
                            title={riskConfig.riskRegister.title}
                        />
                    )}
                    auth={ability.can('view', 'RiskRegister')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.productivity.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={ProductivityPage}
                    auth={ability.can('view', 'RunRate')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.productivity.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.productivity.link}`}
                            title={executionConfig.productivity.title}
                        />
                    )}
                    auth={ability.can('view', 'RunRate')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.resourcesAnalysis.link}/:id([0-9-]+)/:versionToCompareId([0-9-]+)?`}
                    component={ResourcesAnalysisPage}
                    auth={ability.can('view', 'ResourcesAllocation')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.resourcesAnalysis.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.resourcesAnalysis.link}`}
                            title={executionConfig.resourcesAnalysis.title}
                        />
                    )}
                    auth={ability.can('view', 'ResourcesAllocation')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${executionConfig.crossVersionsAnalysis.link}/:id`}
                    component={TrendsPage}
                    auth={ability.can('view', 'TrendAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${executionConfig.crossVersionsAnalysis.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${executionConfig.crossVersionsAnalysis.link}`}
                            title={executionConfig.crossVersionsAnalysis.title}
                        />
                    )}
                    auth={ability.can('view', 'TrendAnalysis')}
                    redirect={`${path}/no-access`}
                />

                <GuardedRoute
                    path={`${path}/${riskConfig.criticalityAnalysis.link}/:id`}
                    component={CriticalityAndRiskPage}
                    auth={ability.can('view', 'CriticalityAnalysis')}
                    redirect={`${path}/no-access`}
                />
                <GuardedRoute
                    path={`${path}/${riskConfig.criticalityAnalysis.link}`}
                    component={() => (
                        <RedirectToLast
                            to={`${path}/${riskConfig.criticalityAnalysis.link}`}
                            title={riskConfig.criticalityAnalysis.title}
                        />
                    )}
                    auth={ability.can('view', 'CriticalityAnalysis')}
                    redirect={`${path}/no-access`}
                />

                <Route path={`${path}/no-access`} component={NoAccess} />
                <Route exact path={path}>
                    <Redirect to={`${path}/${redirect?.link}`} />
                </Route>
                <Route path={'*'} component={PageNotFound} />
            </Switch>
        </Responsive>
    );
};
